// ==========================
// Windsurf: Pass-First AI Exam Questions Preloader
// ==========================

import { Question } from '../types/passFirstExam';

// Sample question structure - replace with actual question data
const createSampleQuestion = (id: string, question: string, options: string[], correctIndex: number): Question => ({
  id,
  question,
  text: question,
  options: options.map((text, index) => ({ id: `option_${index}`, text })),
  answerId: `option_${correctIndex}`,
  correctAnswerId: `option_${correctIndex}`,
  explanation: 'Explanation for this question',
  subject: 'General',
  topic: 'General'
});

// Dutch Questions (NL)
const dutchQuestions: Question[] = [
  createSampleQuestion(
    'nl_1',
    'Wat moet u doen als u een stopbord ziet?',
    [
      'Onmiddellijk stoppen',
      'Snelheid verminderen en voorzichtig zijn',
      'Doorrijden als er geen verkeer is',
      'Claxonneren'
    ],
    0
  ),
  createSampleQuestion(
    'nl_2',
    'Wie heeft voorrang op een kruispunt zonder verkeerslichten?',
    [
      'De bestuurder van rechts',
      'De bestuurder van links',
      'De bestuurder die het eerst komt',
      'De snelste bestuurder'
    ],
    0
  ),
  createSampleQuestion(
    'nl_3',
    'Wat is de maximumsnelheid binnen de bebouwde kom?',
    [
      '30 km/u',
      '50 km/u',
      '70 km/u',
      '100 km/u'
    ],
    1
  ),
  createSampleQuestion(
    'nl_4',
    'Wanneer mag u de linkerrijstrook gebruiken op een snelweg?',
    [
      'Altijd',
      'Alleen voor inhalen',
      'Al bij files',
      'Nooit'
    ],
    1
  ),
  createSampleQuestion(
    'nl_5',
    'Wat betekent een geel doorlopende streep?',
    [
      'Verboden te parkeren',
      'Verboden te stoppen',
      'Scheiding van rijrichtingen',
      'Inhaalverbod'
    ],
    2
  )
];

// Arabic Questions (AR)
const arabicQuestions: Question[] = [
  createSampleQuestion(
    'ar_1',
    'ماذا يجب أن تفعل عندما ترى لافتة توقف؟',
    [
      'التوقف فوراً',
      'تقليل السرعة والحذر',
      'الاستمرار في القيادة إذا لم يكن هناك مرور',
      'الضغط على البوق'
    ],
    0
  ),
  createSampleQuestion(
    'ar_2',
    'من له الأفضلية في تقاطع بدون إشارات مرور؟',
    [
      'السائق من اليمين',
      'السائق من اليسار',
      'السائق الذي يصل أولاً',
      'أسرع سائق'
    ],
    0
  ),
  createSampleQuestion(
    'ar_3',
    'ما هي السرعة القصوى داخل المناطق المبنية؟',
    [
      '30 كم/ساعة',
      '50 كم/ساعة',
      '70 كم/ساعة',
      '100 كم/ساعة'
    ],
    1
  ),
  createSampleQuestion(
    'ar_4',
    'متى يمكنك استخدام المسار الأيسر على الطريق السريع؟',
    [
      'دائماً',
      'للتجاوز فقط',
      'فقط في الازدحام المروري',
      'أبداً'
    ],
    1
  ),
  createSampleQuestion(
    'ar_5',
    'ماذا تعني الخطوة المتقطعة الصفراء؟',
    [
      'ممنوع الوقوف',
      'ممنوع التوقف',
      'فصل بين اتجاهات السير',
      'ممنوع التجاوز'
    ],
    2
  )
];

// English Questions (EN)
const englishQuestions: Question[] = [
  createSampleQuestion(
    'en_1',
    'What should you do when you see a stop sign?',
    [
      'Stop immediately',
      'Slow down and be careful',
      'Continue if there is no traffic',
      'Use the horn'
    ],
    0
  ),
  createSampleQuestion(
    'en_2',
    'Who has the right of way at an intersection without traffic lights?',
    [
      'Driver from the right',
      'Driver from the left',
      'First driver to arrive',
      'Fastest driver'
    ],
    0
  ),
  createSampleQuestion(
    'en_3',
    'What is the maximum speed limit in built-up areas?',
    [
      '30 km/h',
      '50 km/h',
      '70 km/h',
      '100 km/h'
    ],
    1
  ),
  createSampleQuestion(
    'en_4',
    'When can you use the left lane on a highway?',
    [
      'Always',
      'Only for overtaking',
      'Only in traffic jams',
      'Never'
    ],
    1
  ),
  createSampleQuestion(
    'en_5',
    'What does a solid yellow line mean?',
    [
      'No parking',
      'No stopping',
      'Separation of traffic directions',
      'No overtaking'
    ],
    2
  )
];

// ==========================
// Preloaded AI Exams
export const AIExamQuestions: Record<'NL' | 'AR' | 'EN', Question[]> = {
  NL: dutchQuestions,
  AR: arabicQuestions,
  EN: englishQuestions
};

// ==========================
// Question Loading Functions
export function loadQuestionsForLanguage(language: 'NL' | 'AR' | 'EN'): Question[] {
  return AIExamQuestions[language] || [];
}

export function preloadAllQuestions(): Record<'NL' | 'AR' | 'EN', Question[]> {
  return AIExamQuestions;
}

export function getQuestionById(language: 'NL' | 'AR' | 'EN', questionId: string): Question | null {
  const questions = AIExamQuestions[language];
  return questions.find(q => q.id === questionId) || null;
}

export function getRandomQuestions(language: 'NL' | 'AR' | 'EN', count: number): Question[] {
  const questions = AIExamQuestions[language];
  const shuffled = [...questions].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, questions.length));
}
